import { Container, Sprite } from '../../../odie';
import { ColorId, SirenBlockProps } from '../../defs/block';
import { blockPositionView } from '../../util/blockTools';

// types
//-----------------------------------------------------------------------------
export type SirenColor = Exclude<ColorId, 'all' | 'purple'>;
export type Siren = { color: SirenColor; sprite: Sprite };
export type Sirens = [
  Siren | undefined,
  Siren | undefined,
  Siren | undefined,
  Siren | undefined,
];
export type SirenKey = 0 | 1 | 2 | 3;

// constants
//-----------------------------------------------------------------------------
const manifest = {
  base: 'siren/block.siren.png',
};

const sirenScraps: {
  [key in SirenColor]: {
    asset: string;
    scale: number;
  };
} = {
  blue: {
    asset: 'siren/scrap.siren.blue.png',
    scale: 0.65,
  },
  red: {
    asset: 'siren/scrap.siren.red.png',
    scale: 0.65,
  },
  yellow: {
    asset: 'siren/scrap.siren.yellow.png',
    scale: 0.65,
  },
  green: {
    asset: 'siren/scrap.siren.green.png',
    scale: 0.65,
  },
};

const nameToId: Record<SirenColor, number> = {
  blue: 1,
  red: 2,
  yellow: 3,
  green: 4,
};

const idToName: Record<number, SirenColor> = Object.entries(nameToId).reduce(
  (acc, [key, value]) => {
    acc[value] = key as SirenColor;
    return acc;
  },
  {} as Record<number, SirenColor>,
);

const sirenScrapsPosition: { x: number; y: number; z: number }[] = [
  { x: -13, y: 1, z: 0 },
  { x: 42, y: 1, z: 0 },
  { x: -13, y: 56, z: 1 },
  { x: 42, y: 56, z: 1 },
];

/*
    siren block view
*/
export class SirenBlockView extends Container {
  // fields
  //-------------------------------------------------------------------------
  // scene
  private _sirens: Sirens = [undefined, undefined, undefined, undefined];
  private bg: Sprite;

  // properties
  //-------------------------------------------------------------------------
  public get sirens(): Sirens {
    return this._sirens;
  }

  // init
  //-------------------------------------------------------------------------
  constructor(props: SirenBlockProps) {
    super();

    // create border
    this.bg = Sprite.from(manifest.base);

    this.addChild(this.bg);

    // complete scene
    blockPositionView(this, props);
    this.bg.x = -76;
    this.bg.y = -74;

    this.bg.width = 152;
    this.bg.height = 152;
  }

  // api
  //-------------------------------------------------------------------------
  public isEmpty(): boolean {
    return !this._sirens.some((x) => !!x);
  }

  public enable(key: SirenKey, color: SirenColor) {
    // spawn siren sprite
    this._spawnSiren(key, color);
  }

  public disable(key: SirenKey) {
    return this._despawnSiren(key as SirenKey);
  }

  public disableColor(color: ColorId) {
    const key = this._sirens.findIndex((siren) => siren?.color === color);

    if (key === -1) return false;

    return this.disable(key as SirenKey);
  }

  private _spawnSiren(key: SirenKey, color: SirenColor) {
    const props = sirenScraps[color];
    if (!props) return;

    // create siren sprite
    const siren = Sprite.from(props.asset);

    siren.width = 68 * 0.9;
    siren.height = 73 * 0.9;

    const paddingX = 15;
    const paddingY = 10;
    const offsetX = this.bg.x + paddingX;
    const offsetY = this.bg.y + paddingY;
    const step = (this.bg.width - 2 * paddingX) / 2;
    siren.x = offsetX + step * (key % 2);
    siren.y = offsetY + step * Math.floor(key / 2);

    // add to table
    this._sirens[key] = { color: color, sprite: siren };

    // spawn
    this.addChild(siren);
    this.sortChildren();
  }

  private _despawnSiren(key: SirenKey) {
    let removed = false;
    // get siren sprite
    const siren = this._sirens[key];

    if (siren) {
      // remove from scene
      this.removeChild(siren.sprite);

      // remove from table
      this._sirens[key] = undefined;
      removed = true;
    }

    return removed;
  }

  public getGameplayState(): number {
    let defOption = 10000;

    // Encode the siren colors into a number
    for (let i = 0; i < this._sirens.length; i++) {
      const siren = this._sirens[i];
      const offset = Math.pow(10, i);

      defOption += offset * (siren ? nameToId[siren.color] : 0);
    }

    return defOption;
  }

  public setGameplayState(defOption: number = 11234) {
    let option = defOption;

    // Decode the siren colors from a number
    for (let i = 0; i < this._sirens.length; i++) {
      const offset = Math.pow(10, i);
      const colorId = Math.floor(option / offset) % 10;
      const color = idToName[colorId] ?? idToName[0];

      if (color) {
        this.enable(i as SirenKey, color);
      } else {
        this.disable(i as SirenKey);
      }
    }
  }

  public getSirenSprite(color: ColorId) {
    const siren = this._sirens.find((siren) => siren?.color === color);
    return siren?.sprite;
  }

  public static assets(): string[] {
    return [
      manifest.base,
      ...Object.values(sirenScraps).map((object) => object.asset),
    ];
  }
}
